import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAppContext } from "../../../context/AppContext";
import Sidebar from "../../../components/Sidebar";
import ApiService from "../../../services/ApiService";
import Loader from "../../../components/loader";
import Toast from "../../../components/Toast";
import SectionLoader from "../../../components/SectionLoader";
import { getOrCreateDeviceId } from "../../../utils/deviceId";
import Swal from "sweetalert2";
import { isDriverVisibleMaterial } from "../../../utils/lineItemFilters";
const Home: React.FC = () => {
  const vendor_id = localStorage.getItem("vendor_id");
  const api_token = localStorage.getItem("_token");
  const deliveryID = localStorage.getItem("delivery_id");
  const sales_order_id = localStorage.getItem("sales_order_id");
  const { 
    salesOrder, 
    attachments, 
    delivery, 
    loads, 
    purchaseOrder, 
    setDelivery,
    setPurchaseOrder,
    setToken,
    setSalesOrder,
    setLoads,
    setPhotos,
    setAttachments,
    setLoadItems,
    setMeterialSo,
    setMaterialfo,
    setProgressDeliveries,
  } = useAppContext();

  const navigate = useNavigate();
  const [showButton, setShowButton] = useState(false);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [isResuming, setIsResuming] = useState<boolean>(false);
  const [driverName, setDriverName] = useState<string>("");
  const [toast, setToast] = useState<{  message: string;  type: "success" | "error";duration:number; } | null>(null);
  
  // Scroll control for entire page
  const [showScrollUp, setShowScrollUp] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(false);

  // Resume functionality + real-time updates for attachments and loads
  useEffect(() => {
    if (!api_token || !vendor_id || isResuming) return;
    
    let isMounted = true;
    let refreshIntervalId: NodeJS.Timeout | null = null;

    const resumeDelivery = async (skipResumingFlag = false) => {
      // If already have delivery in context, check if we need to resume
      if (delivery && delivery.status && delivery.status !== 'open' && delivery.status !== 'completed') {
        if (!skipResumingFlag) {
          redirectToCurrentStep(delivery.status);
        }
        return;
      }

      // Try to resume active delivery
      if (vendor_id && sales_order_id) {
        if (!skipResumingFlag) {
          setIsResuming(true);
        }
        try {
          const deviceId = getOrCreateDeviceId();
          const responseData = await ApiService.getFreshData({
            vendor_id: vendor_id,
            sales_order_id: sales_order_id,
            delivery_id: deliveryID || null,
            token: false,
            device_id: deviceId,
          });

          if (!isMounted) return;

          if (responseData && responseData.success) {
            if (responseData.cancelled) {
              setToast({ message: "This Order has been Cancelled.", type: "error", duration: 1500});
              localStorage.clear();
              return;
            }

            // Update context with fresh data (including new attachments from Zoho)
            setPurchaseOrder(responseData.data.purchase_order);
            setToken(responseData.api_token);
            setDelivery(responseData.data.delivery);
            setLoads(responseData.data.loads);
            setLoadItems(responseData.data.item_loads);
            setPhotos(responseData.data.photos || []);
            // Always update attachments - they can change from Zoho
            setAttachments(responseData.data.attachments || []);
            setSalesOrder(responseData.data.sales_order);
            setMeterialSo(responseData.data.meterial_so);
            setMaterialfo(responseData.data.material_fo);
            setProgressDeliveries(responseData.data.progress);

            // Update localStorage
            if (responseData.api_token) {
              localStorage.setItem("_token", responseData.api_token);
            }
            if (responseData.data.sales_order) {
              localStorage.setItem("sales_order_id", responseData.data.sales_order.id);
            }
            // Only update delivery_id if delivery exists and belongs to this device
            if (responseData.data.delivery && responseData.data.delivery.id) {
              localStorage.setItem("delivery_id", responseData.data.delivery.id || "");
            } else {
              // Clear stale delivery_id if no delivery found
              localStorage.removeItem("delivery_id");
            }

            // If delivery exists and is in progress, redirect to current step (only on initial load)
            if (!skipResumingFlag && responseData.data.delivery && responseData.data.delivery.status) {
              redirectToCurrentStep(responseData.data.delivery.status);
            }
          } else if (responseData && !responseData.success) {
            // If API returns unsuccessful but not cancelled, log and continue
            // Don't redirect to 403/404 immediately - might be temporary issue
            console.warn("getFreshData returned unsuccessful:", responseData.message);
            // Only show error if it's a critical failure, not a temporary one
            if (responseData.message && responseData.message.includes("unauthorized")) {
              // Only redirect if truly unauthorized, not temporary backend delay
              setTimeout(() => {
                if (!localStorage.getItem("_token")) {
                  window.location.href = "/403";
                }
              }, 2000);
            }
          }
        } catch (error: any) {
          console.error("Error resuming delivery:", error);
          // Don't redirect to 403/404 on network errors or temporary API issues
          // Only redirect if it's a clear unauthorized error
          const errorMessage = error?.message || "";
          if (errorMessage.includes("401") || errorMessage.includes("403")) {
            // Check if token exists - if not, then redirect after a delay
            setTimeout(() => {
              if (!localStorage.getItem("_token")) {
                window.location.href = "/403";
              }
            }, 1000);
          }
          // Don't show error if it's just a fresh session or temporary network issue
        } finally {
          if (isMounted && !skipResumingFlag) {
            setIsResuming(false);
          }
        }
      }
    };

    // Initial resume on mount
    resumeDelivery();

    // Poll every 30 seconds to catch:
    // - New attachments from Zoho
    // - Loads updates (when other drivers accept/complete)
    refreshIntervalId = setInterval(() => {
      if (!document.hidden && isMounted) {
        resumeDelivery(true); // Skip resuming flag for polling updates
      }
    }, 30000);

    // Refresh when tab becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden && isMounted) {
        resumeDelivery(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMounted = false;
      if (refreshIntervalId) {
        clearInterval(refreshIntervalId);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

useEffect(() => {
  if (loads?.total_loads !== 0 && delivery && delivery?.status !== "open") {
    const timer = setTimeout(() => {
      setShowButton(true);
    }, 1000);
    return () => clearTimeout(timer);
  } else {
    // Reset showButton if no delivery or delivery is open
    setShowButton(false);
  }
}, [loads, delivery]);

  // Handle scroll visibility for entire page
  useEffect(() => {
    const checkScrollPosition = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;
      const scrollThreshold = 100; // Threshold to avoid flickering
      
      setShowScrollUp(scrollTop > scrollThreshold);
      setShowScrollDown(scrollTop < scrollHeight - clientHeight - scrollThreshold);
    };

    // Check initial position
    checkScrollPosition();
    
    // Listen to scroll events on window
    window.addEventListener('scroll', checkScrollPosition, { passive: true });
    window.addEventListener('resize', checkScrollPosition, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', checkScrollPosition);
      window.removeEventListener('resize', checkScrollPosition);
    };
  }, []);

  // Redirect to current step based on delivery status
  const redirectToCurrentStep = (status: string) => {
    // switch (status) {
    //   case 'assigned':
    //     navigate('/pick-up');
    //     break;
    //   case 'picked_up':
    //     navigate('/upload-weight-ticket');
    //     break;
    //   case 'reached':
    //     navigate('/proof-of-dump-meterial');
    //     break;
    //   case 'completed':
    //     // Stay on home or navigate to completion
    //     break;
    //   default:
    //     // Stay on home for 'open' status
    //     break;
    // }
  };

  if (!purchaseOrder && !salesOrder && !isResuming) return <SectionLoader />;
  if (!api_token) return <Loader />;

  // Format delivery date with proper validation
  const formatDeliveryDate = () => {
    const shipmentDate = salesOrder?.so_data?.shipment_date || salesOrder?.expected_delivery_date || salesOrder?.order_date;
    if (!shipmentDate) return "N/A";
    
    try {
      // Parse date string directly without timezone conversion
      const dateStr = String(shipmentDate).split('T')[0]; // Get only date part (YYYY-MM-DD)
      const [year, month, day] = dateStr.split('-');
      
      if (!year || !month || !day) return "N/A";
      
      // Format as MM/DD/YYYY without timezone conversion
      return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
    } catch (error) {
      return "N/A";
    }
  };

  const DeliveryDate = formatDeliveryDate();

  // Format customer address - remove duplicates and clean up
  const formatCustomerAddress = () => {
    const address = salesOrder?.customer_address;
    if (!address) return "N/A";
    
    // If address is already a string, clean it up
    if (typeof address === 'string') {
      // Remove duplicate consecutive values (e.g., "islamabad, islamabad" -> "islamabad")
      const parts = address.split(',').map(part => part.trim());
      const uniqueParts = parts.filter((part, index) => {
        // Remove duplicates - only if same value appears consecutively
        return index === 0 || part !== parts[index - 1];
      });
      return uniqueParts.join(', ');
    }
    
    // If address is an object, format it
    if (typeof address === 'object') {
      const parts = [
        address.address,
        address.street2,
        address.city,
        address.state,
        address.zip,
        address.country
      ].filter(Boolean);
      return parts.join(', ');
    }
    
    return address;
  };

  const customer_Address = formatCustomerAddress();
  const salesPersonName = salesOrder?.so_data?.sales_rep?.name || "";

  // Parse quarry shipping address from purchase order
  const formatQuarryAddress = () => {
    const addr = purchaseOrder?.shipping_address;
    if (!addr) return "";
    try {
      const parsed = typeof addr === "string" ? JSON.parse(addr) : addr;
      const parts = [parsed?.address, parsed?.city, parsed?.state, parsed?.zip].filter(Boolean);
      return parts.join(", ");
    } catch {
      return "";
    }
  };

// const deliveryInfo = {
//   saleRep: salesPersonName || "N/A",
//   phone: salesOrder?.so_data?.sales_rep?.custom_cell || "N/A",
//   customerName: salesOrder?.customer_name,
//   customerAddress: customer_Address,
//   deliveryDate: DeliveryDate,
//   vendorName: purchaseOrder?.vendor_name || "",
//   quarryAddress: formatQuarryAddress(),

//   // NEW: keep materials as array (instead of merging)
//   materials: (purchaseOrder?.po_data?.line_items ?? []).filter(
//     (item: any) => item.unit !== "Load"
//   ),

//   notes: salesOrder?.special_instructions || "",
// };
const deliveryInfo = {
  saleRep: salesPersonName || "N/A",
  phone: salesOrder?.so_data?.sales_rep?.custom_cell || "N/A",
  customerName: salesOrder?.customer_name,
  customerAddress: customer_Address,
  deliveryDate: DeliveryDate,
  vendorName: purchaseOrder?.vendor_name || "",
  quarryAddress: formatQuarryAddress(),

  // Only real materials — hides service/misc items (Misc Fees, Pallet Charge,
  // Environmental Fee, Fuel Surcharge, trucking) per client requirement
  materials: (purchaseOrder?.po_data?.line_items ?? []).filter(
    isDriverVisibleMaterial
  ),

  notes: salesOrder?.special_instructions || "",
};
  const ConfirmAndStartDelivery = async () => {
    const result = await Swal.fire({
      title: "Confirm Departure",
      html: `
        <div style="text-align: left; margin-top: 10px;">
          <p style="color: #666; font-size: 14px; margin-bottom: 16px;">
            By accepting this load, you confirm that you are ready to depart to the quarry immediately.
          </p>
          <label style="display: flex; align-items: center; gap: 10px; cursor: pointer; padding: 12px; border: 2px solid #e0e0e0; border-radius: 8px; background: #fafafa;">
            <input type="checkbox" id="swal-depart-checkbox" style="width: 20px; height: 20px; accent-color: #E07B00; cursor: pointer;" />
            <span style="font-weight: 600; color: #333; font-size: 14px;">I am ready to depart to the quarry now</span>
          </label>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Accept Load",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#E07B00",
      cancelButtonColor: "#6c757d",
      preConfirm: () => {
        const checkbox = document.getElementById("swal-depart-checkbox") as HTMLInputElement;
        if (!checkbox?.checked) {
          Swal.showValidationMessage("You must confirm you are ready to depart");
          return false;
        }
        return true;
      },
    });

    if (!result.isConfirmed) {
      return;
    }

    StartDelivery();
  };

  const StartDelivery = async () => {
    setButtonLoading(true);
    let responseData = null;

    try {
      const deviceId = getOrCreateDeviceId();
      // Only send delivery_id if delivery exists and belongs to this device
      // If delivery is null or doesn't match, don't send delivery_id to let backend create new one
      const deliveryIdToSend = (delivery && delivery.id && delivery.device_id === deviceId) 
        ? delivery.id 
        : null;
      
      responseData = await ApiService.startDelivery({
        sales_order_id: salesOrder?.id,
        delivery_id: deliveryIdToSend,
        vendor_id: vendor_id || null,
        device_id: deviceId,
        driver_name: driverName.trim(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      if (responseData && responseData.success) {
       
        setToast({ message: responseData.message || "Delivery Started, proceed to pick up", type: "success", duration:1500});
        setDelivery(responseData.data);
         
        // Calculate and save delivery number for this delivery
        const completedLoads = loads?.completed_loads || 0;
        const inProgressLoads = loads?.in_progress_loads || 0;
        const deliveryNumber = completedLoads + inProgressLoads + 1;
        localStorage.setItem("delivery_number", deliveryNumber.toString());
         

        // Update localStorage with new delivery_id
        if (responseData.data && responseData.data.id) {
          localStorage.setItem("delivery_id", responseData.data.id);
        }
        
        setTimeout(() => {
          localStorage.setItem('page','pick-up')
          navigate("/pick-up");
        }, 500);

      } else {
        setToast({ message: responseData.message, type: "error", duration:1500});
        setButtonLoading(false);
      }
    } catch (error: any) {
      console.log('error.message',error.message);
      const parsed = JSON.parse(error.message || {});
      setToast({ message: parsed?.message || "Start Delivery Failed, Server Error", type: "error",duration: 0 });
      setButtonLoading(false);
    }
  };

  const CancelDelivery = async () => {
    // Show confirmation dialog
    const result = await Swal.fire({
      title: "Cancel Delivery?",
      text: "Are you sure you want to cancel this delivery? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Cancel Delivery",
      cancelButtonText: "No, Keep It",
    });

    if (!result.isConfirmed) {
      return;
    }

    setButtonLoading(true);
    const currentDeliveryId = delivery?.id || deliveryID;
    
    if (!currentDeliveryId) {
      setToast({
        message: "Delivery ID not found. Please refresh the page.",
        type: "error",
        duration: 1500,
      });
      setButtonLoading(false);
      return;
    }

    try {
      const deviceId = getOrCreateDeviceId();
      const responseData = await ApiService.cancelDelivery({
        delivery_id: currentDeliveryId,
        vendor_id: vendor_id || null,
        device_id: deviceId,
      });

      if (responseData && responseData.success) {
        setToast({
          message: responseData.message || "Delivery cancelled successfully",
          type: "success",
          duration: 2000,
        });

        // Reset showButton state
        setShowButton(false);

        // Clear delivery from context and localStorage
        setDelivery(null);
        localStorage.removeItem("delivery_id");

        // Refresh data to update "In Progress" count
        const freshData = await ApiService.getFreshData({
          vendor_id: vendor_id,
          sales_order_id: sales_order_id,
          delivery_id: null,
          token: false,
          device_id: deviceId,
        });

        if (freshData && freshData.success) {
          setPurchaseOrder(freshData.data.purchase_order);
          setToken(freshData.api_token);
          setDelivery(freshData.data.delivery);
          setLoads(freshData.data.loads);
          setLoadItems(freshData.data.item_loads);
          setPhotos(freshData.data.photos || []);
          setAttachments(freshData.data.attachments || []);
          setSalesOrder(freshData.data.sales_order);
          setMeterialSo(freshData.data.meterial_so);
          setMaterialfo(freshData.data.material_fo);
          setProgressDeliveries(freshData.data.progress);
          
          // Ensure showButton is false if no delivery or delivery is open
          if (!freshData.data.delivery || freshData.data.delivery.status === "open") {
            setShowButton(false);
          }
        }

        setButtonLoading(false);
      } else {
        setToast({
          message: responseData.message || "Failed to cancel delivery",
          type: "error",
          duration: 2000,
        });
        setButtonLoading(false);
      }
    } catch (error: any) {
      console.error("Error cancelling delivery:", error);
      const parsed = error.message ? JSON.parse(error.message || {}) : {};
      setToast({
        message: parsed?.message || "Failed to cancel delivery. Please try again.",
        type: "error",
        duration: 2000,
      });
      setButtonLoading(false);
    }
  };

  return (
    <>
      <div className="container-fluid">
        <div className="row">
          <Sidebar />

          <div className="col-lg-9 col-12 content-area">
            <div className="row g-2">
              <div className="col-md-12 mt-0">
                <div className="card-dark align-items-center py-3 mb-2 px-lg-4 px-3">
                  <h6 className="fw-bold mb-2">
                    Decorative Stone Solution Sales Rep:
                  </h6>
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div>
                      <p className="mb-0">
                        {deliveryInfo.saleRep}
                      </p>
                    </div>
                    <div className="d-flex gap-3">
                      <Link to={`tel:${deliveryInfo.phone}`} className="text-decoration-none">
                        <i className="bi bi-telephone-fill secondary-text">
                          {deliveryInfo.phone}
                        </i>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="row g-2 mb-2">
              <div className="col-md-6">
                <div className="card-dark py-2 px-lg-4 px-3">
                  <h5 className="fw-bold">Delivery Location:</h5>
                  <div className="d-flex mt-1">
                    <p className="mb-0 small order-text">
                      <strong className=" card-text mb-1 me-2  secondary-text">
                        Customer Name:
                      </strong>
                      {deliveryInfo.customerName}
                    </p>
                  </div>
                  <div className="d-flex customer-address">
                    <div className="mb-0 small order-text">
                      <strong className=" card-text mb-1  me-2 secondary-text">
                        Jobsite Address:
                      </strong>
                      {deliveryInfo.customerAddress}
                    </div>
                  </div>
                  <div className="d-flex ">
                    <p className="mb-0 small order-text">
                      <strong className="mb-1  me-2 secondary-text card-text">
                        {" "}
                        Delivery Date:{" "}
                      </strong>{" "}
                      {deliveryInfo.deliveryDate}
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card-dark p-2">
                  <h5 className="mb-1 fw-bold"> 
                  Material Pickup Information
                  </h5>
                  <p className="mb-1 ">
                    <span className="order-text">
                      <strong className="secondary-text card-text ">
                        Vendor Name:{" "}
                      </strong>{" "}
                      {deliveryInfo.vendorName}
                    </span>
                  </p>
                  {deliveryInfo.quarryAddress && (
                    <p className="mb-1">
                      <span className="order-text">
                        <strong className="secondary-text card-text">
                          Quarry Address:{" "}
                        </strong>{" "}
                        {deliveryInfo.quarryAddress}
                      </span>
                    </p>
                  )}
               <div className="mb-2">
  <p className="mb-1">
    <span className="order-text">
      <strong className="secondary-text card-text">
        DSS Material:
      </strong>
    </span>
  </p>

  {deliveryInfo.materials?.length > 0 ? (
    <div style={{ paddingLeft: "6px" }}>
      {deliveryInfo.materials.map((item: any, index: number) => (
        <div
          key={index}
          className="d-flex justify-content-between align-items-center order-text"
          style={{
            padding: "6px 0",
            borderBottom:
              index !== deliveryInfo.materials.length - 1
                ? "1px dashed rgba(255,255,255,0.2)"
                : "none",
          }}
        >
          {/* LEFT SIDE WITH NUMBERING + BOLD */}
         <span>
  <strong>
    Material {index + 1}:
  </strong>{" "}
  <strong>
    {item.description || item.name}
  </strong>
</span>

          {/* RIGHT SIDE QUANTITY (GREEN NOW) */}
          <span
            style={{
              background: "#28a745",
              color: "#fff",
              padding: "3px 10px",
              borderRadius: "12px",
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            {parseFloat(item.quantity || 0).toFixed(2)} {item.unit}
          </span>
        </div>
      ))}

      {/* TOTAL ROW (NOW ORANGE) */}
   {/* TOTAL ROW (NOW ORANGE) */}
      <div
        className="d-flex justify-content-between align-items-center order-text"
        style={{
          padding: "8px 0 4px",
          marginTop: "4px",
          borderTop: "2px solid rgba(255,255,255,0.3)",
          fontWeight: "600",
        }}
      >
        <span>
          <strong>Total Quantity</strong>
        </span>

        <span
          style={{
            background: "#E07B00",
            color: "#fff",
            padding: "4px 12px",
            borderRadius: "12px",
            fontWeight: 700,
          }}
        >
          {deliveryInfo.materials
            .reduce(
              (total: number, item: any) =>
                total + (parseFloat(item.quantity) || 0),
              0
            )
            .toFixed(2)}{" "}
          {deliveryInfo.materials[0]?.unit || ""}
        </span>
      </div>
    </div>
  ) : (
    <span className="order-text"> N/A </span>
  )}
</div>
                </div>
              </div>
            </div>
            {deliveryInfo.notes && (
              <>
                <h4 className="mb-1 mt-3 fw-semibold headings">
                  Additional delivery notes for drivers
                </h4>
                <div className="card-light text-center py-2 px-lg-5 px-2 mb-2">
                  <h6 className="mb-0">{deliveryInfo.notes}</h6>
                </div>
              </>
            )}
            {Array.isArray(attachments) && attachments.length > 0 && (
              <div className="row g-0 mb-4">
                <div className="col-md-12">
                  <div 
                    className="card-dark d-block"
                    style={{
                      padding: '5px',
                      borderRadius: '16px',
                      background: 'linear-gradient(135deg, #1D3340 0%, #2a4758 100%)',
                    }}
                  >
                    {/* Minimal Header */}
                    <div className="d-flex align-items-center gap-2 mb-4">
                      <div 
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          background: 'rgba(224, 123, 0, 0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <i className="bi bi-paperclip" style={{ fontSize: '16px', color: '#E07B00' }}></i>
                      </div>
                      <h6 
                        className="mb-0 fw-semibold" 
                        style={{ 
                          color: '#fff',
                          fontSize: '16px',
                          letterSpacing: '-0.2px',
                        }}
                      >
                       Aditional delivery instruction
                      </h6>
                      <div 
                        style={{
                          marginLeft: 'auto',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          background: 'rgba(255, 255, 255, 0.1)',
                          fontSize: '12px',
                          color: 'rgba(255, 255, 255, 0.9)',
                          fontWeight: '500',
                        }}
                      >
                        {attachments.length} {attachments.length === 1 ? 'file' : 'files'}
                      </div>
                    </div>

                    {/* Minimal File List */}
                    <div className="d-flex flex-column gap-2">
                      {attachments.map((attachment: any, index: number) => (
                        <Link
                          key={attachment.id || attachment.file_name || index}
                          to={attachment?.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            textDecoration: 'none',
                            display: 'block',
                          }}
                        >
                          <div
                            className="d-flex align-items-center gap-3"
                            style={{ 
                              backgroundColor: "#fff", 
                              borderRadius: "12px", 
                              border: '1px solid rgba(0, 0, 0, 0.06)',
                              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                              cursor: 'pointer',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-1px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                              e.currentTarget.style.borderColor = 'rgba(224, 123, 0, 0.3)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                              e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.06)';
                            }}
                          >
                            {/* Minimal File Icon */}
                            <div 
                              style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '10px',
                                background: 'linear-gradient(135deg, #fff5e6 0%, #ffe0b3 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              <i className="bi bi-file-earmark-text" style={{ fontSize: '18px', color: '#E07B00' }}></i>
                            </div>

                            {/* File Info */}
                            <div 
                              className="flex-grow-1" 
                              style={{ 
                                minWidth: 0,
                                overflow: 'hidden',
                              }}
                            >
                              <div 
                                style={{ 
                                  fontSize: '14px',
                                  color: '#1a1a1a',
                                  fontWeight: '500',
                                  lineHeight: '1.4',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  marginBottom: '2px',
                                }}
                                title={attachment?.file_name}
                              >
                                {attachment?.file_name}
                              </div>
                              <div 
                                style={{
                                  fontSize: '11px',
                                  color: '#8b8b8b',
                                  fontWeight: '400',
                                  letterSpacing: '0.2px',
                                }}
                              >
                                Tap to open
                              </div>
                            </div>

                            {/* Minimal Download Icon */}
                            <div
                              className="download-icon-wrapper"
                              style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '8px',
                                background: '#f5f5f5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                transition: 'all 0.25s ease',
                              }}
                            >
                              <i 
                                className="bi bi-download download-icon" 
                                style={{ 
                                  fontSize: '14px', 
                                  color: '#666',
                                  transition: 'color 0.25s ease',
                                }}
                              ></i>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Page Scroll Buttons - Fixed Position */}
            {showScrollUp && (
              <button
                onClick={() => {
                  window.scrollBy({
                    top: -300,
                    behavior: 'smooth'
                  });
                }}
                style={{
                  position: 'fixed',
                  top: '100px',
                  right: '20px',
                  zIndex: 1000,
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  backgroundColor: '#E07B00',
                  color: '#fff',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(224, 123, 0, 0.5)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#c86900';
                  e.currentTarget.style.transform = 'scale(1.15)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(224, 123, 0, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#E07B00';
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(224, 123, 0, 0.5)';
                }}
                aria-label="Scroll up"
              >
                <i className="bi bi-chevron-up" style={{ fontSize: '24px', fontWeight: 'bold' }}></i>
              </button>
            )}

            {showScrollDown && (
              <button
                onClick={() => {
                  window.scrollBy({
                    top: 300,
                    behavior: 'smooth'
                  });
                }}
                style={{
                  position: 'fixed',
                  bottom: '100px',
                  right: '20px',
                  zIndex: 1000,
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  backgroundColor: '#E07B00',
                  color: '#fff',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(224, 123, 0, 0.5)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#c86900';
                  e.currentTarget.style.transform = 'scale(1.15)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(224, 123, 0, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#E07B00';
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(224, 123, 0, 0.5)';
                }}
                aria-label="Scroll down"
              >
                <i className="bi bi-chevron-down" style={{ fontSize: '24px', fontWeight: 'bold' }}></i>
              </button>
            )}

            {loads?.total_loads !== 0 && (!delivery || delivery?.status === "open") && (
              <div className="row g-2 mt-2 mb-2">
                <div className="col-md-12">
                  <div
                    style={{
                      background: "#ffffff",
                      borderRadius: "12px",
                      padding: "16px",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                      borderLeft: "4px solid #E07B00",
                    }} 
                  >
                    <div className="d-flex align-items-center" style={{ marginBottom: "12px" }}>
                      <div
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "50%",
                          background: "linear-gradient(135deg, #E07B00, #f5a623)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginRight: "10px",
                          flexShrink: 0,
                        }}
                      >
                        <i className="bi bi-person-fill" style={{ fontSize: "1.1rem", color: "#fff" }}></i>
                      </div>
                      <div>
                        <h6 className="fw-bold mb-0" style={{ color: "#1a1a1a", fontSize: "1rem" }}>
                          Driver Name <span style={{ color: "#dc2626" }}>*</span>
                        </h6>
                      </div>
                    </div>

                    <input
                      type="text"
                      value={driverName}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^a-zA-Z\s.]/g, "");
                        setDriverName(value);
                      }}
                      placeholder="First Name & Last Initial (e.g. John D.)"
                      style={{
                        fontSize: "1rem",
                        padding: "12px 14px",
                        borderRadius: "8px",
                        border: "2px solid #e0e0e0",
                        width: "100%",
                        outline: "none",
                        color: "#333",
                        background: "#fafafa",
                        transition: "all 0.2s ease",
                        marginBottom: "10px",
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = "#E07B00";
                        e.currentTarget.style.background = "#fff";
                        e.currentTarget.style.boxShadow = "0 0 0 3px rgba(224, 123, 0, 0.15)";
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = "#e0e0e0";
                        e.currentTarget.style.background = "#fafafa";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    />

                    <p className="mb-0 fw-semibold text-center" style={{ color: "#dc2626", fontSize: "0.78rem", lineHeight: "1.3" }}>
                      <i className="bi bi-exclamation-triangle-fill" style={{ marginRight: "5px" }}></i>
                      Only hit accept when you are ready to depart for the quarry
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="d-flex justify-content-center mt-2 mb-3 flex-wrap gap-2">
              {loads?.total_loads === 0 && (
                <button disabled={true} className="btn-orange">
                  Loads Not Found
                </button>
              )}

              {loads?.total_loads !== 0 && (!delivery || delivery?.status === "open") && (
                <button
                  disabled={buttonLoading || !driverName.trim()}
                  onClick={() => ConfirmAndStartDelivery()}
                  className="btn-orange"
                  style={{ opacity: !driverName.trim() ? 0.5 : 1 }}
                >
                  {buttonLoading && (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  )}
                  Accept load and start delivery
                </button>
              )}
              {/* {loads?.total_loads !== 0 && delivery && delivery?.status !== "open" && ( */}
              {showButton && (
                <>
                  <button
                    disabled={buttonLoading}
                    onClick={() => navigate("/pick-up")}
                    className="btn-orange"
                  >
                    {buttonLoading && (
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                    )}
                    Next
                  </button>
                  {delivery && delivery?.status && delivery?.status !== "open" && delivery?.status !== "completed" && (
                    <button
                      disabled={buttonLoading}
                      onClick={() => CancelDelivery()}
                      className="btn-light-orange"
                      style={{ 
                        backgroundColor: "#dc3545",
                        borderColor: "#dc3545",
                        color: "white"
                      }}
                    >
                      {buttonLoading && (
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                      )}
                      Cancel Load
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default Home;
